import { CartErrorEvent, CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { formatPriceCode } from '@theme/money-formatting';

/**
 * SSC pack selector (blocks/ssc-pack-selector.liquid).
 *
 * The chosen option ("1 PAIR", "3-PACK", "SUBSCRIBE") sets the hidden quantity (and selling plan)
 * input that belongs to the product form, so Add to cart, Buy now and the sticky bar (which clicks
 * the main button) all send the pack. After every size change the label of the Add to cart button
 * and the sticky price are rewritten for the pack, the style links keep size and pack, and a pack
 * that the size has too few pairs for falls back to one pair. ?pack=1 preselects one pair.
 *
 * Mix your pack: with the pack chosen, steppers split it across the styles (e.g. 2 Classic + 1 Stay
 * Salty, always adding up to the pack size, same size for all). A pack of only the current style
 * goes through the theme's product form as usual; a mixed pack is added here instead (Add to cart and
 * the sticky bar through the form's submit, Buy now through mixedItems()), like ssc-pack-builder.
 */
class SscPackSelector extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;
  /** @type {MutationObserver | undefined} */
  #observer;
  #writing = false;
  #adding = false;
  /** @type {Record<string, { price: number, available: boolean, stock: number | null, size: string }>} */
  #variants = {};

  connectedCallback() {
    // Quick add stays one pair: the dialog clones the product page, so step out there
    if (this.closest('dialog, quick-add-dialog')) {
      this.querySelectorAll('input[form]').forEach((input) => input.remove());
      return;
    }

    this.#controller = new AbortController();
    const { signal } = this.#controller;
    try {
      this.#variants = JSON.parse(this.querySelector('[data-pack-variants]')?.textContent || '{}');
    } catch {
      this.#variants = {};
    }

    const params = new URLSearchParams(window.location.search);
    if (params.get('pack') === '1') {
      const single = this.#options().find((option) => option.value === '1');
      if (single) single.checked = true;
    }

    this.addEventListener('change', () => this.#apply(), { signal });
    this.addEventListener('click', (event) => this.#onStep(event), { signal });
    // Capture phase on window runs before the theme's own (document-level) submit routing
    window.addEventListener('submit', (event) => this.#onSubmit(event), { capture: true, signal });
    this.closest('.shopify-section')?.addEventListener(
      StandardEvents.productSelect,
      (event) => {
        if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
        Promise.resolve(event.promise)
          .then(() => requestAnimationFrame(() => this.#apply()))
          .catch(() => {});
      },
      { signal }
    );

    // The product form morphs its button (and the sticky bar its price) after a size change:
    // write the pack label back whenever they change
    this.#observer = new MutationObserver(() => {
      if (!this.#writing) this.#apply();
    });
    const button = this.#button();
    const stickyPrice = this.#stickyPrice();
    if (button) this.#observer.observe(button, { childList: true, characterData: true, subtree: true });
    if (stickyPrice) this.#observer.observe(stickyPrice, { childList: true, characterData: true, subtree: true });

    this.#apply();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#observer?.disconnect();
  }

  /** @returns {HTMLElement[]} */
  #mixRows() {
    return Array.from(this.querySelectorAll('[data-mix-row]'));
  }

  /** @param {HTMLElement} row */
  #mixCount(row) {
    return Number(row.querySelector('[data-mix-count]')?.textContent) || 0;
  }

  /**
   * @param {HTMLElement} row
   * @param {number} count
   */
  #setMixCount(row, count) {
    const counter = row.querySelector('[data-mix-count]');
    if (counter) counter.textContent = String(count);
  }

  /**
   * The variant of a mix row in the selected size
   * @param {HTMLElement} row
   * @returns {{ id: number, available: boolean, stock: number | null } | undefined}
   */
  #mixVariant(row) {
    const size = this.#variants[this.#variantId()]?.size;
    if (!size) return undefined;
    try {
      return JSON.parse(row.dataset.variants || '{}')[size];
    } catch {
      return undefined;
    }
  }

  /**
   * @param {HTMLElement} row
   * @param {number} count
   */
  #mixFits(row, count) {
    if (count === 0) return true;
    const variant = this.#mixVariant(row);
    return Boolean(variant?.available && (variant.stock === null || variant.stock >= count));
  }

  /** @param {MouseEvent} event */
  #onStep(event) {
    const button = event.target instanceof Element ? event.target.closest('[data-mix-step]') : null;
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;
    const row = button.closest('[data-mix-row]');
    if (!(row instanceof HTMLElement)) return;
    const step = Number(button.dataset.mixStep);
    const rows = this.#mixRows();
    const others = rows.filter((other) => other !== row);
    // Keep the total at the pack size: take from (or give to) the current style first, then the others
    const order = [...others.filter((other) => other.hasAttribute('data-current')), ...others.filter((other) => !other.hasAttribute('data-current'))];
    const partner = step > 0 ? order.find((other) => this.#mixCount(other) > 0) : order.find((other) => this.#mixFits(other, this.#mixCount(other) + 1));
    if (!partner) return;
    this.#setMixCount(row, this.#mixCount(row) + step);
    this.#setMixCount(partner, this.#mixCount(partner) - step);
    this.#apply();
    const status = this.querySelector('[data-mix-status]');
    if (status) {
      status.textContent = rows
        .filter((item) => this.#mixCount(item) > 0)
        .map((item) => `${this.#mixCount(item)} ${item.dataset.name}`)
        .join(', ');
    }
  }

  /** True when the pack is chosen and split across more than the current style */
  #isMixed() {
    const selected = this.#options().find((option) => option.checked);
    if (!selected || selected.value === '1' || selected.dataset.plan) return false;
    return this.#mixRows().some((row) => !row.hasAttribute('data-current') && this.#mixCount(row) > 0);
  }

  /**
   * Cart lines for a mixed pack, or null when the product form's own add applies (Buy now uses this)
   * @returns {{ id: number, quantity: number }[] | null}
   */
  mixedItems() {
    if (!this.#isMixed()) return null;
    const items = [];
    for (const row of this.#mixRows()) {
      const quantity = this.#mixCount(row);
      if (!quantity) continue;
      const variant = this.#mixVariant(row);
      if (!variant) return null;
      items.push({ id: variant.id, quantity });
    }
    return items.length ? items : null;
  }

  /** @param {SubmitEvent} event */
  #onSubmit(event) {
    if (event.target !== this.#form()) return;
    const items = this.mixedItems();
    if (!items) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.#addMixed(items);
  }

  /** @param {{ id: number, quantity: number }[]} items */
  async #addMixed(items) {
    if (this.#adding) return;
    this.#adding = true;
    const button = this.#button();
    button?.setAttribute('aria-busy', 'true');

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : undefined))
      .filter(Boolean);
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map(({ id, quantity }) => ({ merchandiseId: String(id), quantity })),
        promise: deferred.promise,
      })
    );

    const fetchCart = async () => {
      const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
      return response.json();
    };

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const data = await response.json();
      if (!response.ok || data.status) {
        this.dispatchEvent(
          new CartErrorEvent({
            error: data.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: data.description, errors: data.errors },
          })
        );
        const cart = await fetchCart().catch(() => null);
        if (cart) {
          deferred.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: { didError: true, items: cart.items, source: 'ssc-pack-selector', itemCount: 0 },
          });
        } else {
          deferred.reject(new Error('Add pack failed'));
        }
        return;
      }
      const cart = await fetchCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'ssc-pack-selector',
          itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
          productId: this.dataset.productId,
          sections: data.sections,
          didError: false,
        },
      });
      // Open the cart drawer once the cart listeners on the event promise have run
      deferred.promise
        .then(() => {
          const drawer = document.getElementById('cart-drawer');
          if (drawer && !drawer.hasAttribute('open') && 'open' in drawer && typeof drawer.open === 'function') drawer.open();
        })
        .catch(() => {});
    } catch (error) {
      console.error(error);
      deferred.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error ? error.message : 'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    } finally {
      this.#adding = false;
      button?.removeAttribute('aria-busy');
    }
  }

  /**
   * Shows the mix while the pack is chosen; steppers follow stock of the selected size
   * @param {boolean} packChosen
   */
  #applyMix(packChosen) {
    const panel = this.querySelector('[data-pack-mix]');
    if (!(panel instanceof HTMLElement)) return;
    panel.hidden = !packChosen;
    const rows = this.#mixRows();
    // A size change can leave a style without enough pairs: start over with the current style
    if (rows.some((row) => !this.#mixFits(row, this.#mixCount(row)))) {
      // Mixed again when the size allows it (e.g. 2 + 1), else the whole pack in the current style
      const quantity = Number(this.dataset.packQuantity) || 3;
      const current = rows.find((row) => row.hasAttribute('data-current'));
      const other = rows.find((row) => row !== current && this.#mixFits(row, 1));
      const mixed = current && other && this.#mixFits(current, quantity - 1);
      rows.forEach((row) => {
        if (row === current) this.#setMixCount(row, mixed ? quantity - 1 : quantity);
        else this.#setMixCount(row, mixed && row === other ? 1 : 0);
      });
    }
    for (const row of rows) {
      const count = this.#mixCount(row);
      const minus = row.querySelector('[data-mix-step="-1"]');
      const plus = row.querySelector('[data-mix-step="1"]');
      const others = rows.filter((other) => other !== row);
      if (minus instanceof HTMLButtonElement) {
        minus.disabled = count === 0 || !others.some((other) => this.#mixFits(other, this.#mixCount(other) + 1));
      }
      if (plus instanceof HTMLButtonElement) {
        plus.disabled = !this.#mixFits(row, count + 1) || !others.some((other) => this.#mixCount(other) > 0);
      }
    }
  }

  /** @returns {HTMLInputElement[]} */
  #options() {
    return Array.from(this.querySelectorAll('input[data-pack-option]'));
  }

  #form() {
    const id = this.querySelector('[data-pack-quantity-input]')?.getAttribute('form');
    return id ? document.getElementById(id) : null;
  }

  #button() {
    const form = this.#form();
    return form?.closest('product-form-component')?.querySelector('[ref="addToCartButton"]') ?? null;
  }

  #stickyPrice() {
    return this.closest('.shopify-section')?.querySelector('sticky-add-to-cart .sticky-add-to-cart__price') ?? null;
  }

  #variantId() {
    const form = this.#form();
    const input = form?.querySelector('input[name="id"]');
    return input instanceof HTMLInputElement ? input.value : '';
  }

  /** Pack price for a single-pair price, as the Liquid computes it */
  #packCents(single) {
    const quantity = Number(this.dataset.packQuantity) || 3;
    const regular = single * quantity;
    const percent = Number(this.dataset.percent) || 0;
    const amount = Number(this.dataset.amount) || 0;
    if (percent > 0) return Math.round((regular * (100 - percent)) / 100);
    if (amount > 0) return regular - amount;
    return regular;
  }

  #apply() {
    const quantity = Number(this.dataset.packQuantity) || 3;
    const currency = this.dataset.currency || '';
    const variant = this.#variants[this.#variantId()];
    const options = this.#options();
    const packOptions = options.filter((option) => option.value !== '1');

    // Too few pairs of this size for a pack: one pair only
    const tooFew = Boolean(variant && variant.stock !== null && variant.stock < quantity);
    packOptions.forEach((option) => (option.disabled = tooFew));
    const stockNote = this.querySelector('[data-pack-stock]');
    if (stockNote instanceof HTMLElement) stockNote.hidden = !tooFew;
    let selected = options.find((option) => option.checked);
    if (!selected || selected.disabled) {
      selected = options.find((option) => option.value === '1');
      if (selected) selected.checked = true;
    }
    if (!selected) return;

    const count = Number(selected.value) || 1;
    const quantityInput = this.querySelector('[data-pack-quantity-input]');
    if (quantityInput instanceof HTMLInputElement) quantityInput.value = String(count);
    const planInput = this.querySelector('[data-pack-plan-input]');
    if (planInput instanceof HTMLInputElement) {
      planInput.value = selected.dataset.plan || '';
      planInput.disabled = !selected.dataset.plan;
    }

    this.#applyMix(count > 1 && !selected.dataset.plan);

    if (!variant) return;
    const single = variant.price;
    const pack = this.#packCents(single);
    const regular = single * quantity;
    const set = (selector, text) => {
      const element = this.querySelector(selector);
      if (element && element.textContent !== text) element.textContent = text;
    };
    set('[data-price-single]', formatPriceCode(single, currency));
    set('[data-price-pack]', formatPriceCode(pack, currency));
    set('[data-price-regular]', formatPriceCode(regular, currency));
    const save = this.querySelector('[data-price-save]');
    if (save) {
      const savePrice = formatPriceCode(regular - pack, currency);
      set('[data-price-save]', (save.getAttribute('data-template') || '[price]').replace('[price]', savePrice));
    }
    const per = this.querySelector('[data-price-per]');
    if (per) {
      const perPrice = formatPriceCode(Math.round(pack / quantity), currency);
      set('[data-price-per]', (per.getAttribute('data-template') || '[price]').replace('[price]', perPrice));
    }

    // Style links keep size and pack
    this.querySelectorAll('[data-style-link]').forEach((link) => {
      if (!(link instanceof HTMLAnchorElement)) return;
      try {
        const map = JSON.parse(link.dataset.variants || '{}');
        const url = new URL(link.href, window.location.origin);
        if (map[variant.size]) url.searchParams.set('variant', String(map[variant.size]));
        url.searchParams.set('pack', String(count));
        link.href = url.pathname + url.search;
      } catch {}
    });

    // Add to cart label and sticky price for the chosen option (subscriptions keep the server price)
    if (selected.dataset.plan || !variant.available) return;
    const total = count === 1 ? single : pack;
    const price = formatPriceCode(total, currency);
    this.#writing = true;
    const button = this.#button();
    if (button instanceof HTMLButtonElement && !button.disabled) {
      const template = this.dataset.atcTemplate || '[price]';
      const label = template.replace('[price]', price);
      const textNode = Array.from(button.childNodes).find(
        (node) => node.nodeType === Node.TEXT_NODE && node.textContent && node.textContent.trim() !== ''
      );
      if (textNode && textNode.textContent?.trim() !== label) textNode.textContent = label;
    }
    // Write into the price text inside the sticky bar so its markup and styling stay
    const stickyPrice = this.#stickyPrice();
    const stickyText = stickyPrice?.querySelector('.price bdi, .price') ?? stickyPrice;
    if (stickyText && stickyText.textContent?.trim() !== price) stickyText.textContent = price;
    // Let the observer see our own writes as done before it reacts again
    queueMicrotask(() => {
      this.#writing = false;
    });
  }
}

if (!customElements.get('ssc-pack-selector')) customElements.define('ssc-pack-selector', SscPackSelector);
